import { z } from "zod";
import type * as GeoJSON from "geojson";

function isPosition(value: unknown): value is GeoJSON.Position {
  return (
    Array.isArray(value) &&
    value.length >= 2 &&
    value.every((item) => typeof item === "number" && Number.isFinite(item)) &&
    value[0] >= -180 &&
    value[0] <= 180 &&
    value[1] >= -90 &&
    value[1] <= 90
  );
}

function isLinearRing(value: unknown): value is GeoJSON.Position[] {
  if (!Array.isArray(value) || value.length < 4 || !value.every(isPosition)) {
    return false;
  }
  const first = value[0];
  const last = value[value.length - 1];
  return first[0] === last[0] && first[1] === last[1];
}

function isPolygonCoordinates(value: unknown): value is GeoJSON.Position[][] {
  return Array.isArray(value) && value.length > 0 && value.every(isLinearRing);
}

export const wgs84PolygonGeometrySchema = z.custom<
  GeoJSON.Polygon | GeoJSON.MultiPolygon
>(
  (value) => {
    if (!value || typeof value !== "object" || !("type" in value)) return false;
    if (value.type === "Polygon") {
      return "coordinates" in value && isPolygonCoordinates(value.coordinates);
    }
    return (
      value.type === "MultiPolygon" &&
      "coordinates" in value &&
      Array.isArray(value.coordinates) &&
      value.coordinates.length > 0 &&
      value.coordinates.every(isPolygonCoordinates)
    );
  },
  { message: "Expected a non-empty WGS84 Polygon or MultiPolygon" },
);

export const wgs84PropertyGeometrySchema = z.custom<GeoJSON.GeoJsonObject>(
  (value) => {
    if (wgs84PolygonGeometrySchema.safeParse(value).success) return true;
    if (
      !value ||
      typeof value !== "object" ||
      !("type" in value) ||
      value.type !== "FeatureCollection" ||
      !("features" in value) ||
      !Array.isArray(value.features) ||
      value.features.length === 0
    ) {
      return false;
    }
    return value.features.every(
      (feature) =>
        feature?.type === "Feature" &&
        wgs84PolygonGeometrySchema.safeParse(feature.geometry).success,
    );
  },
  { message: "Expected non-empty WGS84 property polygon evidence" },
);
